import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Avatar, AvatarFallback, AvatarImage } from '../components/ui/avatar';
import { Mail, Phone, Calendar, Shield, User, Building, Briefcase, Clock } from 'lucide-react';
import { formatMemberSinceDate, getRoleColor, getStatusColor } from '@/helpers/common';

interface TeamMemberDetailsProps {
  isOpen: boolean;
  onClose: () => void;
  member: any;
}

export function TeamMemberDetails({ isOpen, onClose, member }: TeamMemberDetailsProps) {
  if (!member) return null;

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[600px] max-h-[80vh] overflow-y-auto bg-popover">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-3">
            <Avatar className="w-10 h-10">
              <AvatarImage src={member.avatar} alt={`${member.firstname} ${member.lastname}`} />
              <AvatarFallback className="bg-priamry text-white text-lg">
                {(member.firstname?.[0] || '')}{(member.lastname?.[0] || '')}
              </AvatarFallback>
            </Avatar>
            <div>
              <div className="font-semibold">{member.firstname} {member.lastname}</div>
              <div className="text-sm text-muted-foreground">{member.emailid}</div>
            </div>
          </DialogTitle>
          <DialogDescription>
            Detailed information about this team member
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-6">
          <div className="space-y-4">
            <h3 className="text-lg font-semibold flex items-center gap-2">
              <User className="w-5 h-5" />
              Basic Information
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-3">
                <div className="flex items-center gap-2 text-sm">
                  <Mail className="w-4 h-4 text-muted-foreground" />
                  <span className="font-medium">Email:</span>
                  <span>{member.emailid}</span>
                </div>
                {member.mobile && (
                  <div className="flex items-center gap-2 text-sm">
                    <Phone className="w-4 h-4 text-muted-foreground" />
                    <span className="font-medium">Phone:</span>
                    <span>{member.mobile}</span>
                  </div>
                )}
                <div className="flex items-center gap-2 text-sm">
                  <Calendar className="w-4 h-4 text-muted-foreground" />
                  <span className="font-medium">Joined:</span>
                  <span>{formatMemberSinceDate(member.registeredon)}</span>
                </div>
              </div>
              <div className="space-y-3">
                <div className="flex items-center gap-2 text-sm">
                  <Briefcase className="w-4 h-4 text-muted-foreground" />
                  <span className="font-medium">Designation:</span>
                  <span>{member.designation || 'N/A'}</span>
                </div>
                <div className="flex items-center gap-2 text-sm">
                  <Building className="w-4 h-4 text-muted-foreground" />
                  <span className="font-medium">Department:</span>
                  <span>{member.department || 'N/A'}</span>
                </div>
                <div className="flex items-center gap-2 text-sm">
                  <Clock className="w-4 h-4 text-muted-foreground" />
                  <span className="font-medium">Status:</span>
                  <Badge className={getStatusColor(member.status)}>
                    {member.status}
                  </Badge>
                </div>
              </div>
            </div>
          </div>

          <div className="space-y-4">
            <h3 className="text-lg font-semibold flex items-center gap-2">
              <Shield className="w-5 h-5" />
              Role and Permissions
            </h3>
            <div className="space-y-3">
              <div className="flex items-center gap-2">
                <span className="font-medium text-sm">Role:</span>
                <Badge className={getRoleColor(member.role)}>
                  {member.role || 'N/A'}
                </Badge>
              </div>
              {member.permissions && member.permissions.length > 0 && (
                <div>
                  <span className="font-medium text-sm">Permissions:</span>
                  <div className="flex flex-wrap gap-2 mt-2">
                    {member.permissions.map((permission: string, index: number) => (
                      <Badge key={index} variant="outline" className="text-xs">
                        <Shield className="w-3 h-3 mr-1" />
                        {permission}
                      </Badge>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>

        
          <div className="space-y-4">
            <h3 className="text-lg font-semibold flex items-center gap-2">
              <Clock className="w-5 h-5" />
              Activity Statistics
            </h3>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {member.total_scans_created !== undefined && (
                <div className="text-center p-3 bg-muted/50 rounded-lg">
                  <div className="text-2xl font-bold text-primary">{member.total_scans_created}</div>
                  <div className="text-xs text-muted-foreground">Scans Created</div>
                </div>
              )}
              {member.total_projects_created !== undefined && (
                <div className="text-center p-3 bg-muted/50 rounded-lg">
                  <div className="text-2xl font-bold text-primary">{member.total_projects_created}</div>
                  <div className="text-xs text-muted-foreground">Projects Created</div>
                </div>
              )}
              {member.completed_scans !== undefined && (
                <div className="text-center p-3 bg-muted/50 rounded-lg">
                  <div className="text-2xl font-bold text-primary">{member.completed_scans}</div>
                  <div className="text-xs text-muted-foreground">Completed Scans</div>
                </div>
              )}
              {member.failed_scans !== undefined && (
                <div className="text-center p-3 bg-muted/50 rounded-lg">
                  <div className="text-2xl font-bold text-primary">{member.failed_scans}</div>
                  <div className="text-xs text-muted-foreground">Failed Scans</div>
                </div>
              )}
            </div>
          </div>

     
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            Close
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}